
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { cva } from "class-variance-authority";
import { TrendingUp, TrendingDown } from "lucide-react";

const cardVariants = cva(
  "relative overflow-hidden rounded-xl border transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5",
  {
    variants: {
      variant: {
        default: "bg-white border-slate-200",
        success: "bg-gradient-to-br from-emerald-500 to-teal-600 border-transparent text-white",
        warning: "bg-gradient-to-br from-amber-400 to-orange-500 border-transparent text-white",
        info: "bg-gradient-to-br from-blue-500 to-indigo-600 border-transparent text-white",
        danger: "bg-gradient-to-br from-red-500 to-rose-600 border-transparent text-white",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

const iconContainerVariants = cva(
  "flex items-center justify-center h-12 w-12 rounded-xl transition-transform group-hover:scale-110",
  {
    variants: {
      variant: {
        default: "bg-blue-50 text-blue-600",
        success: "bg-white/20 text-white",
        warning: "bg-white/20 text-white",
        info: "bg-white/20 text-white",
        danger: "bg-white/20 text-white",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

interface StatisticsCardProps {
  title: string;
  value: string | number;
  description?: string;
  icon: React.ReactNode;
  variant?: "default" | "success" | "warning" | "info" | "danger";
  trend?: {
    value: number;
    isPositive: boolean;
  };
  className?: string;
}

const StatisticsCard = ({
  title,
  value,
  description,
  icon,
  variant = "default",
  trend,
  className,
}: StatisticsCardProps) => {
  const isColoredCard = variant !== "default";

  return (
    <Card className={cn(cardVariants({ variant }), "group", className)}>
      {/* Decorative background elements */}
      {isColoredCard && (
        <>
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-black/5 rounded-full translate-y-1/2 -translate-x-1/2" />
        </>
      )}

      <CardContent className="relative p-6">
        <div className="flex items-start justify-between">
          <div className="space-y-3">
            <p className={cn(
              "text-sm font-medium",
              isColoredCard ? "text-white/80" : "text-slate-500"
            )}>
              {title}
            </p>

            <div className="flex items-baseline gap-2">
              <span className={cn(
                "text-3xl font-bold tracking-tight",
                isColoredCard ? "text-white" : "text-slate-900"
              )}>
                {value}
              </span>
            </div>

            {description && (
              <p className={cn(
                "text-sm",
                isColoredCard ? "text-white/70" : "text-slate-500"
              )}>
                {description}
              </p>
            )}

            {trend && (
              <div className="flex items-center gap-1 pt-1">
                {trend.isPositive ? (
                  <TrendingUp className={cn(
                    "h-4 w-4",
                    isColoredCard ? "text-white" : "text-emerald-500"
                  )} />
                ) : (
                  <TrendingDown className={cn(
                    "h-4 w-4",
                    isColoredCard ? "text-white" : "text-red-500"
                  )} />
                )}
                <span className={cn(
                  "text-sm font-medium",
                  isColoredCard
                    ? "text-white"
                    : trend.isPositive ? "text-emerald-600" : "text-red-600"
                )}>
                  {trend.isPositive ? "+" : "-"}{Math.abs(trend.value)}%
                </span>
                <span className={cn(
                  "text-xs",
                  isColoredCard ? "text-white/60" : "text-slate-400"
                )}>
                  από προηγ. περίοδο
                </span>
              </div>
            )}
          </div>

          <div className={iconContainerVariants({ variant })}>
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default StatisticsCard;
